import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/dateUtils";
import { X, CalendarClock, Users, Clock, Repeat, Sparkles, Activity, Wifi, Loader2 } from "lucide-react";

const QUALITY_TINT = {
  Good: "bg-emerald-100 text-emerald-700 border-emerald-300",
  Fair: "bg-amber-100 text-amber-700 border-amber-300",
  Poor: "bg-red-100 text-red-700 border-red-300",
};

function NetBadge({ net, testid }) {
  if (!net || (!net.quality && net.rtt_ms == null)) {
    return <span className="text-neutral-400" data-testid={testid}>—</span>;
  }
  return (
    <span
      data-testid={testid}
      title={`avg RTT ${net.rtt_ms ?? "?"} ms · ${net.downlink_mbps ?? "?"} Mbps · ${net.samples} samples`}
      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${QUALITY_TINT[net.quality] || "bg-neutral-100 text-neutral-600 border-neutral-300"}`}
    >
      <Wifi size={10} /> {net.quality || "n/a"}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, hint }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-md p-4" title={hint}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">{label}</div>
        <Icon size={14} className="text-neutral-400" />
      </div>
      <div className="font-display font-bold text-2xl mt-1.5">{value}</div>
    </div>
  );
}

export function TeacherActivityModal({ user, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null); setError("");
    if (!user) return;
    api.get(`/admin/users/${user.id}/activity`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load activity"));
  }, [user]);

  if (!user) return null;
  const t = data?.totals;
  const maxUse = Math.max(1, ...(data?.feature_usage || []).map((f) => f.count));

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="teacher-activity-modal" role="dialog" aria-modal="true">
      <div className="bg-[#FBFAF7] w-full max-w-5xl rounded-lg border border-neutral-200 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 pb-4 border-b border-neutral-200 sticky top-0 bg-[#FBFAF7] z-10">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 inline-flex items-center gap-1.5">
              <Activity size={11} /> teacher activity dashboard
            </div>
            <h3 className="font-display font-bold text-2xl tracking-tighter mt-1">{user.full_name || user.email}</h3>
            <div className="text-xs text-neutral-500 mt-0.5">
              {user.email} · {data?.user?.plan || user.plan || "trial"} plan · Joined {fmtDate(data?.user?.created_at || user.created_at)}
            </div>
          </div>
          <button onClick={onClose} data-testid="activity-modal-close" className="text-neutral-400 hover:text-[#0A0C10]"><X size={20} /></button>
        </div>

        {error ? (
          <div className="p-10 text-center text-sm text-red-600" data-testid="activity-error">{error}</div>
        ) : !data ? (
          <div className="p-14 flex items-center justify-center gap-2 text-neutral-500 text-sm"><Loader2 size={16} className="animate-spin" /> Loading activity…</div>
        ) : (
          <div className="p-6 space-y-7">
            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="activity-totals">
              <StatCard label="Total classes" value={t.classes} icon={CalendarClock} hint={`${t.completed_classes} completed`} />
              <StatCard label="Class minutes" value={t.scheduled_minutes.toLocaleString()} icon={Clock} hint="Total scheduled minutes" />
              <StatCard label="Recurring" value={t.recurring_classes} icon={Repeat} hint="Classes that are part of a recurring series" />
              <StatCard label="Students" value={t.students_registered} icon={Users} hint="Students registered under this teacher" />
              <StatCard label="AI credits used" value={`≈${t.est_ai_credits_used}`} icon={Sparkles} hint={`Estimated · ${data.user.ai_credits_left ?? 0} credits left`} />
              <StatCard label="Live time" value={`${Math.round(t.live_session_seconds / 60)}m`} icon={Activity} hint="Actual in-room session time across all their classes" />
            </div>

            {/* Feature usage ranking */}
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">/ most used features</div>
              <div className="bg-white border border-neutral-200 rounded-md divide-y divide-neutral-100" data-testid="activity-feature-usage">
                {data.feature_usage.every((f) => f.count === 0) ? (
                  <div className="p-4 text-sm text-neutral-400">No AI content created yet.</div>
                ) : data.feature_usage.filter((f) => f.count > 0).map((f) => (
                  <div key={f.key} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="w-40 text-sm font-medium truncate">{f.label}</div>
                    <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#0A0C10] rounded-full" style={{ width: `${(f.count / maxUse) * 100}%` }} />
                    </div>
                    <div className="w-24 text-right text-xs font-mono text-neutral-600">{f.count} · ≈{f.est_credits} cr</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Day-wise classes */}
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">/ classes day-wise</div>
              {data.day_wise.length === 0 ? (
                <div className="bg-white border border-neutral-200 rounded-md p-4 text-sm text-neutral-400">No classes scheduled yet.</div>
              ) : (
                <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="activity-day-wise">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-left text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">
                      <tr>
                        <th className="px-4 py-2.5">Date</th>
                        <th className="px-4 py-2.5">Classes</th>
                        <th className="px-4 py-2.5">Minutes</th>
                        <th className="px-4 py-2.5">Titles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.day_wise.slice(0, 30).map((d) => (
                        <tr key={d.date} className="border-t border-neutral-100">
                          <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{fmtDate(d.date)}</td>
                          <td className="px-4 py-2 font-semibold">{d.classes}</td>
                          <td className="px-4 py-2">{d.minutes}</td>
                          <td className="px-4 py-2 text-neutral-500 text-xs truncate max-w-[380px]">{d.titles.join(" · ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Per-class detail incl. internet quality */}
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">/ recent classes · internet quality</div>
              {data.recent_classes.length === 0 ? (
                <div className="bg-white border border-neutral-200 rounded-md p-4 text-sm text-neutral-400">No classes yet.</div>
              ) : (
                <div className="bg-white border border-neutral-200 rounded-md overflow-x-auto" data-testid="activity-classes">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-left text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">
                      <tr>
                        <th className="px-4 py-2.5">Date</th>
                        <th className="px-4 py-2.5">Class</th>
                        <th className="px-4 py-2.5">Mins</th>
                        <th className="px-4 py-2.5">Students</th>
                        <th className="px-4 py-2.5">Type</th>
                        <th className="px-4 py-2.5">Status</th>
                        <th className="px-4 py-2.5">Teacher net</th>
                        <th className="px-4 py-2.5">Student net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_classes.slice(0, 30).map((c) => (
                        <tr key={c.id} className="border-t border-neutral-100">
                          <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{fmtDate(c.start_time)}</td>
                          <td className="px-4 py-2 font-medium truncate max-w-[220px]">{c.title}</td>
                          <td className="px-4 py-2">{c.duration_minutes || "—"}</td>
                          <td className="px-4 py-2">{c.students}</td>
                          <td className="px-4 py-2 text-xs">{c.recurring ? <span className="inline-flex items-center gap-1 text-[#6366F1]"><Repeat size={11} /> recurring</span> : "one-off"}</td>
                          <td className="px-4 py-2 text-xs capitalize text-neutral-600">{c.status || "—"}</td>
                          <td className="px-4 py-2"><NetBadge net={c.net?.teacher} testid={`net-teacher-${c.id}`} /></td>
                          <td className="px-4 py-2"><NetBadge net={c.net?.student} testid={`net-student-${c.id}`} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-4 py-2 text-[11px] text-neutral-400 border-t border-neutral-100">
                    Internet quality is sampled live during classes — older classes (before this feature) show "—".
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TeacherActivityModal;
